import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Monitor, Tablet, Smartphone, RefreshCw, ExternalLink, Eye, Code2,
  Database, Activity, Send, Loader2, FileCode, Save, History, Rocket, RotateCcw,
  Sparkles, ChevronRight, CheckCircle2, Terminal, X, Zap, Shield, Gauge, Search,
  Users, Wand2, Trash2, Plus, Radio,
} from "lucide-react";
import api, { API } from "../lib/api";
import { Logo } from "../components/Logo";
import { buildPreview } from "../lib/preview";
import { ModelPicker } from "../components/ModelPicker";

const BUILD_STEPS = ["Understanding requirements", "Designing UX & layouts", "Building the design system", "Writing frontend code", "Wiring backend logic", "Designing the database", "Running security scan", "Executing tests", "Optimizing performance", "Finalizing build"];
const WORK_AGENTS = [
  { name: "Frontend", icon: Code2, color: "text-indigo-400" },
  { name: "Backend", icon: Database, color: "text-emerald-400" },
  { name: "Security", icon: Shield, color: "text-rose-400" },
  { name: "Testing", icon: CheckCircle2, color: "text-cyan-400" },
];
const DEVICES = { desktop: "100%", tablet: "768px", mobile: "390px" };
const SCORE_ICONS = { build: Zap, tests: CheckCircle2, security: Shield, performance: Gauge, seo: Search, accessibility: Eye };

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("preview");
  const [device, setDevice] = useState("desktop");
  const [activeFile, setActiveFile] = useState(null);
  const [fileDraft, setFileDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [buildStep, setBuildStep] = useState(0);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [liveMode, setLiveMode] = useState(true);
  const [streamText, setStreamText] = useState("");
  const [showTeam, setShowTeam] = useState(false);
  const [team, setTeam] = useState({ owner: null, members: [] });
  const [newMember, setNewMember] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [showDeploys, setShowDeploys] = useState(false);
  const [deploys, setDeploys] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [editModel, setEditModel] = useState(null);
  const chatEndRef = useRef(null);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
      setMessages(res.data.messages || []);
      if (res.data.files?.length && !activeFile) {
        const idx = res.data.files.find((f) => f.path.endsWith("index.html")) || res.data.files[0];
        setActiveFile(idx.path);
        setFileDraft(idx.content);
      }
      return res.data;
    } catch {
      toast.error("Project not found");
      navigate("/dashboard");
    }
  }, [id, navigate, activeFile]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  // build progress animation + poll
  useEffect(() => {
    if (project?.status !== "building") return;
    // Advance through steps and HOLD on the last one (never loop back).
    const anim = setInterval(() => setBuildStep((s) => Math.min(s + 1, BUILD_STEPS.length - 1)), 4000);
    const poll = setInterval(load, 3500);
    return () => { clearInterval(anim); clearInterval(poll); };
  }, [project?.status, load]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending, streamText]);

  // poll while autonomous mode is planning or running
  useEffect(() => {
    const st = project?.autonomous?.status;
    if (st !== "running" && st !== "planning") return;
    const t = setInterval(async () => {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
      if (res.data.autonomous?.status === "done") {
        clearInterval(t);
        setPreviewKey((k) => k + 1);
        toast.success("Autonomous build complete — deployed ⚡");
      }
    }, 4000);
    return () => clearInterval(t);
  }, [project?.autonomous?.status, id]);

  // live presence heartbeat
  useEffect(() => {
    if (!project || project.status !== "ready") return;
    let stop = false;
    const beat = async () => {
      try { const res = await api.post(`/projects/${id}/presence`); if (!stop) setViewers(res.data.active || []); } catch {}
    };
    beat();
    const t = setInterval(beat, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [project?.status, id]);

  // cleanup SSE on unmount
  useEffect(() => () => { esRef.current?.close(); }, []);

  // default the edit model to the project's model
  useEffect(() => { if (project?.model && !editModel) setEditModel(project.model); }, [project?.model, editModel]);

  // Escape closes any open drawer
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setShowVersions(false); setShowTeam(false); setShowDeploys(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const selectFile = (f) => { setActiveFile(f.path); setFileDraft(f.content); };
  const currentFile = project?.files?.find((f) => f.path === activeFile);

  const saveFile = async () => {
    try {
      const res = await api.put(`/projects/${id}/file`, { path: activeFile, content: fileDraft });
      setProject((p) => ({ ...p, files: res.data.files }));
      setPreviewKey((k) => k + 1);
      toast.success("Saved · new version created");
    } catch { toast.error("Save failed"); }
  };

  const applyResult = (data) => {
    setProject((p) => ({ ...p, files: data.files, messages: data.messages }));
    setMessages(data.messages || []);
    const cf = data.files.find((f) => f.path === activeFile);
    if (cf) setFileDraft(cf.content);
    setPreviewKey((k) => k + 1);
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { id: `tmp_${Date.now()}`, role: "user", content: msg }]);
    setSending(true);
    setStreamText("");

    if (liveMode) {
      const token = localStorage.getItem("nexora_token");
      const mdl = editModel || project.model || "";
      const url = `${API}/projects/${id}/chat/stream?message=${encodeURIComponent(msg)}&token=${encodeURIComponent(token)}&model=${encodeURIComponent(mdl)}`;
      const es = new EventSource(url);
      esRef.current = es;
      es.onmessage = async (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "delta") {
            setStreamText((t) => (t + ev.content).slice(-1400));
          } else if (ev.type === "done") {
            es.close();
            const res = await api.get(`/projects/${id}`);
            applyResult(res.data);
            setStreamText("");
            setSending(false);
            toast.success("Applied changes");
          } else if (ev.type === "error") {
            es.close();
            setSending(false);
            setStreamText("");
            toast.error("Edit failed — try rephrasing");
          }
        } catch { /* ignore parse */ }
      };
      es.onerror = () => { es.close(); setSending(false); setStreamText(""); toast.error("Connection lost — retry"); };
      return;
    }

    try {
      await api.post(`/projects/${id}/chat`, { message: msg, model: editModel || project.model });
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/projects/${id}`);
          if (!res.data.busy) {
            clearInterval(poll);
            applyResult(res.data);
            setSending(false);
            const last = (res.data.messages || []).slice(-1)[0];
            if (last?.error) toast.error("Edit failed — try rephrasing");
            else toast.success("Applied changes");
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
      setSending(false);
    }
  };

  const startAutonomous = async () => {
    try {
      await api.post(`/projects/${id}/autonomous`, { origin_url: window.location.origin });
      toast.success("Autonomous mode engaged ⚡ — planning improvements");
      setProject((p) => ({ ...p, autonomous: { status: "planning", steps: [] } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start autonomous mode");
    }
  };

  const openDeploys = async () => {
    setShowDeploys(true);
    try { const res = await api.get(`/projects/${id}/deployments`); setDeploys(res.data); } catch {}
  };
  const rollback = async (depId) => {
    try {
      const res = await api.post(`/projects/${id}/deployments/${depId}/rollback`);
      setProject((p) => ({ ...p, files: res.data.files, deployment: res.data.deployment }));
      const cf = res.data.files.find((f) => f.path === activeFile);
      if (cf) setFileDraft(cf.content);
      setPreviewKey((k) => k + 1);
      toast.success("Rolled back to that deploy");
      setShowDeploys(false);
    } catch { toast.error("Rollback failed"); }
  };

  const createInvite = async () => {
    try {
      const res = await api.post(`/projects/${id}/invites`, { role: "developer" });
      const url = `${window.location.origin}/invite/${res.data.token}`;
      setInviteUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Invite link copied to clipboard"); }
      catch { toast.success("Invite link created"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create invite"); }
  };

  const openTeam = async () => {
    setShowTeam(true);
    try { const res = await api.get(`/projects/${id}/members`); setTeam(res.data); } catch {}
  };
  const addMember = async () => {
    if (!newMember.trim()) return;
    try {
      await api.post(`/projects/${id}/members`, { email: newMember.trim(), role: "developer" });
      setNewMember("");
      const res = await api.get(`/projects/${id}/members`);
      setTeam(res.data);
      toast.success("Teammate added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const removeMember = async (email) => {
    try {
      await api.delete(`/projects/${id}/members/${encodeURIComponent(email)}`);
      const res = await api.get(`/projects/${id}/members`);
      setTeam(res.data);
    } catch { toast.error("Could not remove"); }
  };

  const openVersions = async () => {
    setShowVersions(true);
    try { const res = await api.get(`/projects/${id}/versions`); setVersions(res.data.reverse()); } catch {}
  };

  const restore = async (vid) => {
    try {
      const res = await api.post(`/projects/${id}/versions/${vid}/restore`);
      setProject((p) => ({ ...p, files: res.data.files }));
      const cf = res.data.files.find((f) => f.path === activeFile);
      if (cf) setFileDraft(cf.content);
      setPreviewKey((k) => k + 1);
      toast.success("Version restored");
      setShowVersions(false);
    } catch { toast.error("Restore failed"); }
  };

  const deploy = async () => {
    setDeploying(true);
    try {
      const res = await api.post(`/projects/${id}/deploy`, { origin_url: window.location.origin });
      setProject((p) => ({ ...p, deployment: res.data }));
      toast.success("Deployed — your app has a live URL 🚀");
      setTab("preview");
    } catch { toast.error("Deploy failed"); }
    finally { setDeploying(false); }
  };

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  }

  if (project.status === "building") return <BuildingView project={project} step={buildStep} navigate={navigate} />;
  if (project.status === "error") return <ErrorView project={project} navigate={navigate} onRetry={load} />;

  const previewDoc = buildPreview(project.files);
  const scores = project.spec?.scores || {};

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="glass border-b border-white/10 h-14 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="workspace-back-button" onClick={() => navigate("/dashboard")} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"><ArrowLeft className="h-4 w-4" /></button>
          <Logo size="sm" to="/dashboard" />
          <ChevronRight className="h-4 w-4 text-slate-600" />
          <span className="font-medium truncate max-w-[220px]">{project.name}</span>
          {project.deployment?.status === "live" && project.deployment?.url && (
            <a data-testid="live-url-link" href={project.deployment.url} target="_blank" rel="noreferrer"
              className="hidden lg:flex items-center gap-1.5 text-xs font-mono text-emerald-400 px-2 py-1 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {viewers.length > 0 && (
            <div className="hidden md:flex items-center -space-x-2 mr-1" data-testid="presence-viewers" title={`${viewers.length} online`}>
              {viewers.slice(0, 4).map((v) => (
                v.picture
                  ? <img key={v.email} src={v.picture} alt={v.name} title={v.name} className="h-7 w-7 rounded-full object-cover ring-2 ring-[#07090E]" />
                  : <div key={v.email} title={v.name} className="h-7 w-7 rounded-full bg-indigo-600 flex items-center justify-center text-[11px] font-semibold ring-2 ring-[#07090E]">{(v.name || v.email)[0].toUpperCase()}</div>
              ))}
              {viewers.length > 4 && <div className="h-7 w-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] ring-2 ring-[#07090E]">+{viewers.length - 4}</div>}
            </div>
          )}
          <button data-testid="workspace-autonomous-button" onClick={startAutonomous}
            disabled={["running", "planning"].includes(project.autonomous?.status)}
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-50 transition-colors">
            {["running", "planning"].includes(project.autonomous?.status) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            <span className="hidden lg:inline">Autonomous</span>
          </button>
          <button data-testid="workspace-deploys-button" onClick={openDeploys} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-300 hover:bg-white/[0.06] transition-colors"><Rocket className="h-4 w-4" /> <span className="hidden md:inline">Deploys</span></button>
          <button data-testid="workspace-team-button" onClick={openTeam} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-300 hover:bg-white/[0.06] transition-colors"><Users className="h-4 w-4" /> <span className="hidden sm:inline">Team</span></button>
          <button data-testid="workspace-versions-button" onClick={openVersions} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-300 hover:bg-white/[0.06] transition-colors"><History className="h-4 w-4" /> <span className="hidden sm:inline">History</span></button>
          <button data-testid="workspace-deploy-button" onClick={deploy} disabled={deploying}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors text-sm font-semibold">
            {deploying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Deploy
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: file tree */}
        <aside className="w-64 shrink-0 border-r border-white/10 flex flex-col bg-[#0A0D14]">
          <div className="px-4 h-11 flex items-center border-b border-white/10 text-xs font-mono text-slate-500 uppercase tracking-wider">Explorer</div>
          <div className="flex-1 overflow-y-auto p-2">
            <div className="text-xs font-mono text-slate-500 px-2 py-1.5">{project.name}/</div>
            {project.files?.map((f) => (
              <button key={f.path} data-testid={`file-${f.path}`} onClick={() => selectFile(f)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${activeFile === f.path ? "bg-indigo-500/15 text-white" : "text-slate-400 hover:bg-white/[0.04]"}`}>
                <FileCode className="h-3.5 w-3.5 shrink-0 text-indigo-400" /> <span className="truncate">{f.path}</span>
              </button>
            ))}
          </div>
          <div className="border-t border-white/10 p-3 space-y-2">
            <div className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-1">Health</div>
            <div className="grid grid-cols-3 gap-1.5">
              {["build", "tests", "security"].map((k) => (
                <div key={k} className="rounded-lg bg-white/[0.03] p-2 text-center">
                  <div className="font-heading text-sm font-bold text-emerald-400">{scores[k] ?? "—"}</div>
                  <div className="text-[10px] text-slate-500 capitalize">{k}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* CENTER */}
        <main className="flex-1 flex flex-col overflow-hidden bg-[#07090E]">
          <div className="h-11 border-b border-white/10 flex items-center justify-between px-3 shrink-0">
            <div className="flex items-center gap-1">
              {[["preview", Eye, "Preview"], ["code", Code2, "Code"], ["database", Database, "Database"], ["health", Activity, "Health"]].map(([k, Icon, label]) => (
                <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === k ? "bg-white/[0.06] text-white" : "text-slate-400 hover:text-white"}`}>
                  <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
            {tab === "preview" && (
              <div className="flex items-center gap-1">
                {Object.keys(DEVICES).map((d) => {
                  const Icon = d === "desktop" ? Monitor : d === "tablet" ? Tablet : Smartphone;
                  return <button key={d} data-testid={`device-${d}`} onClick={() => setDevice(d)}
                    className={`p-1.5 rounded-lg transition-colors ${device === d ? "bg-white/[0.06] text-white" : "text-slate-500 hover:text-white"}`}><Icon className="h-4 w-4" /></button>;
                })}
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button data-testid="preview-refresh" onClick={() => setPreviewKey((k) => k + 1)} className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors"><RefreshCw className="h-4 w-4" /></button>
                <button data-testid="preview-newtab" onClick={() => { const w = window.open(); w.document.write(previewDoc); w.document.close(); }} className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors"><ExternalLink className="h-4 w-4" /></button>
              </div>
            )}
            {tab === "code" && currentFile && (
              <button data-testid="code-save-button" onClick={saveFile} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-medium transition-colors"><Save className="h-3.5 w-3.5" /> Save</button>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {tab === "preview" && (
              <div className="h-full w-full flex items-center justify-center p-4 overflow-auto bg-[#0A0D14]">
                <div className="h-full transition-all duration-300 bg-white rounded-lg overflow-hidden shadow-2xl" style={{ width: DEVICES[device], maxWidth: "100%" }}>
                  <iframe key={previewKey} title="preview" srcDoc={previewDoc} className="w-full h-full border-0" sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin" />
                </div>
              </div>
            )}
            {tab === "code" && (
              <div className="h-full flex flex-col">
                <div className="px-4 py-2 text-xs font-mono text-slate-500 border-b border-white/10">{activeFile}</div>
                <textarea data-testid="code-editor" value={fileDraft} onChange={(e) => setFileDraft(e.target.value)} spellCheck={false}
                  className="flex-1 w-full bg-[#07090E] text-slate-200 font-mono text-[13px] leading-relaxed p-4 outline-none resize-none" />
              </div>
            )}
            {tab === "database" && <DatabaseView schema={project.spec?.db_schema} />}
            {tab === "health" && <HealthView scores={scores} spec={project.spec} deployment={project.deployment} />}
          </div>
        </main>

        {/* RIGHT: AI chat */}
        <aside className="w-[380px] shrink-0 border-l border-white/10 flex flex-col bg-[#0A0D14]">
          <div className="px-4 h-11 flex items-center gap-2 border-b border-white/10">
            <span className="h-6 w-6 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shrink-0"><Sparkles className="h-3.5 w-3.5 text-white" /></span>
            <span className="text-sm font-medium">Agent Copilot</span>
            <span className="ml-auto text-xs font-mono text-emerald-400 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" /> Online</span>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="glass rounded-xl p-4">
              <div className="text-xs font-mono text-slate-500 mb-2 uppercase tracking-wider">Build summary</div>
              <p className="text-sm text-slate-300 leading-relaxed">{project.spec?.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(project.spec?.tech_stack || []).map((t) => <span key={t} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300">{t}</span>)}
              </div>
            </div>

            {(project.agents_log || []).length > 0 && (
              <div className="glass rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-indigo-300">
                  <Activity className="h-3.5 w-3.5" /> Agent activity
                </div>
                {project.agents_log.map((a, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" />
                    <div><span className="text-indigo-300 font-medium">{a.agent}</span> <span className="text-slate-400">{a.message}</span></div>
                  </div>
                ))}
              </div>
            )}

            {sending && (
              <div data-testid="agent-activity-feed" className="glass rounded-xl p-3.5 space-y-2.5 border-indigo-500/30">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-indigo-300">
                  <Radio className="h-3.5 w-3.5 animate-pulse-glow" /> Agents working
                </div>
                {WORK_AGENTS.map((a, i) => (
                  <div key={a.name} className="flex items-center gap-2.5 text-xs">
                    <span className={`relative h-6 w-6 rounded-lg bg-white/[0.04] flex items-center justify-center ${i === 0 ? "agent-active-ring" : ""}`}>
                      <a.icon className={`h-3.5 w-3.5 ${a.color}`} />
                    </span>
                    <span className="text-slate-300">{a.name} agent</span>
                    <span className="ml-auto text-[10px] font-mono text-slate-500">{i === 0 ? "working…" : "queued"}</span>
                  </div>
                ))}
              </div>
            )}

            {project.autonomous?.status && (
              <div className="glass rounded-xl p-4 border-amber-500/30" data-testid="autonomous-panel">
                <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider text-amber-300">
                  <Wand2 className="h-3.5 w-3.5" /> Autonomous {project.autonomous.status === "done" ? "· complete" : project.autonomous.status === "planning" ? "· planning" : "· running"}
                </div>
                {project.autonomous.status === "planning" && (
                  <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> AI is analyzing your app and planning improvements…</div>
                )}
                <div className="space-y-2">
                  {(project.autonomous.steps || []).map((s, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-xs">
                      {s.status === "done" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" />
                        : s.status === "running" ? <Loader2 className="h-3.5 w-3.5 text-amber-400 animate-spin shrink-0 mt-0.5" />
                        : s.status === "failed" ? <X className="h-3.5 w-3.5 text-rose-400 shrink-0 mt-0.5" />
                        : <div className="h-3.5 w-3.5 rounded-full border border-white/15 shrink-0 mt-0.5" />}
                      <span className={s.status === "pending" ? "text-slate-500" : "text-slate-300"}>{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[90%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-indigo-600 text-white" : "glass text-slate-200"}`}>
                  {m.content}
                  {m.changed_files?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.changed_files.map((f) => <span key={f} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/30 text-cyan-300">{f}</span>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && liveMode && streamText && (
              <div className="flex justify-start" data-testid="stream-output">
                <div className="glass rounded-xl px-3.5 py-2.5 max-w-[90%] w-full">
                  <div className="flex items-center gap-2 text-xs font-mono text-cyan-300 mb-1.5"><Radio className="h-3 w-3 animate-pulse-glow" /> <span className="shimmer-text">streaming</span></div>
                  <pre className="text-[11px] font-mono text-slate-400 whitespace-pre-wrap break-words max-h-40 overflow-hidden leading-relaxed">{streamText}<span className="cursor-blink">▋</span></pre>
                </div>
              </div>
            )}
            {sending && (!liveMode || !streamText) && (
              <div className="flex justify-start"><div className="glass rounded-xl px-3.5 py-2.5 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Agents are working…</div></div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="p-3 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <ModelPicker value={editModel || project.model} onChange={setEditModel} size="sm" />
              <button data-testid="live-mode-toggle" onClick={() => setLiveMode((v) => !v)}
                className={`flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-full transition-colors ${liveMode ? "bg-cyan-500/15 text-cyan-300" : "bg-white/[0.04] text-slate-500"}`}>
                <Radio className="h-3 w-3" /> Live {liveMode ? "on" : "off"}
              </button>
            </div>
            <div className="glass rounded-xl p-2 flex items-end gap-2">
              <textarea data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Describe a change in plain English…" rows={2}
                className="flex-1 bg-transparent resize-none outline-none text-sm px-2 py-1 placeholder:text-slate-600" />
              <button data-testid="ai-chat-send" onClick={send} disabled={sending || !input.trim()}
                className="h-9 w-9 shrink-0 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 flex items-center justify-center transition-colors">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Deploys drawer */}
      <AnimatePresence>
        {showDeploys && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" onClick={() => setShowDeploys(false)}>
            <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "spring", damping: 28 }}
              className="w-[440px] h-full bg-[#0A0D14] border-l border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="h-14 flex items-center justify-between px-5 border-b border-white/10">
                <div className="flex items-center gap-2"><Rocket className="h-4 w-4 text-emerald-400" /><span className="font-medium">Deploy history</span></div>
                <button onClick={() => setShowDeploys(false)} className="p-2 rounded-lg text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {deploys.map((d, i) => (
                  <div key={d.id} data-testid={`deploy-${d.id}`} className="glass rounded-xl p-4 card-hover">
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-2 font-mono text-xs text-emerald-300">
                        {i === 0 && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}v{d.version} {i === 0 && "· current"}
                      </span>
                      {i !== 0 && <button data-testid={`rollback-${d.id}`} onClick={() => rollback(d.id)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Rollback</button>}
                    </div>
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-cyan-300 hover:underline break-all">{d.url}</a>
                    <p className="font-mono text-[11px] text-slate-500 mt-1">{d.by} · {new Date(d.at).toLocaleString()}</p>
                  </div>
                ))}
                {deploys.length === 0 && <p className="text-slate-500 text-sm text-center py-8">No deploys yet — hit Deploy to ship your first version</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Team drawer */}
      <AnimatePresence>
        {showTeam && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" onClick={() => setShowTeam(false)}>
            <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "spring", damping: 28 }}
              className="w-[420px] h-full bg-[#0A0D14] border-l border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="h-14 flex items-center justify-between px-5 border-b border-white/10">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-indigo-400" /><span className="font-medium">Team</span></div>
                <button onClick={() => setShowTeam(false)} className="p-2 rounded-lg text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-5 border-b border-white/10">
                <div className="flex gap-2">
                  <input data-testid="member-email-input" value={newMember} onChange={(e) => setNewMember(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addMember(); }}
                    placeholder="teammate@company.com"
                    className="flex-1 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-indigo-500/60 outline-none text-sm placeholder:text-slate-600" />
                  <button data-testid="add-member-button" onClick={addMember} className="px-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 transition-colors flex items-center gap-1.5 text-sm font-medium"><Plus className="h-4 w-4" /> Add</button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Members can view and edit this project.</p>
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid="create-invite-button" onClick={createInvite} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] transition-colors"><Users className="h-3.5 w-3.5" /> Create invite link</button>
                </div>
                {inviteUrl && (
                  <div data-testid="invite-url" className="mt-2 flex items-center gap-2 rounded-lg bg-black/30 px-3 py-2">
                    <input readOnly value={inviteUrl} className="flex-1 bg-transparent text-[11px] font-mono text-cyan-300 outline-none" />
                    <button onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Copied"); }} className="text-[11px] text-slate-400 hover:text-white">Copy</button>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {team.owner && (
                  <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-semibold">{(team.owner.name || team.owner.email || "O")[0].toUpperCase()}</div>
                      <div><div className="text-sm font-medium">{team.owner.name || team.owner.email}</div><div className="text-xs text-slate-500 font-mono">{team.owner.email}</div></div>
                    </div>
                    <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">Owner</span>
                  </div>
                )}
                {team.members.map((m) => (
                  <div key={m.email} data-testid={`member-${m.email}`} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold">{m.email[0].toUpperCase()}</div>
                      <div><div className="text-sm font-medium font-mono">{m.email}</div><div className="text-xs text-slate-500 capitalize">{m.role}</div></div>
                    </div>
                    <button data-testid={`remove-member-${m.email}`} onClick={() => removeMember(m.email)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {team.members.length === 0 && <p className="text-slate-500 text-sm text-center py-6">No teammates yet</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Versions drawer */}
      <AnimatePresence>
        {showVersions && (          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" onClick={() => setShowVersions(false)}>
            <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "spring", damping: 28 }}
              className="w-[420px] h-full bg-[#0A0D14] border-l border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="h-14 flex items-center justify-between px-5 border-b border-white/10">
                <div className="flex items-center gap-2"><History className="h-4 w-4 text-indigo-400" /><span className="font-medium">Version history</span></div>
                <button onClick={() => setShowVersions(false)} className="p-2 rounded-lg text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {versions.map((v) => (
                  <div key={v.id} data-testid={`version-${v.id}`} className="glass rounded-xl p-4 card-hover">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-indigo-300">v{v.number}</span>
                      <button data-testid={`restore-${v.id}`} onClick={() => restore(v.id)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                    </div>
                    <p className="text-sm text-slate-300">{v.label}</p>
                    <p className="font-mono text-[11px] text-slate-500 mt-1">{new Date(v.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {versions.length === 0 && <p className="text-slate-500 text-sm text-center py-8">No versions yet</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BuildingView({ project, step, navigate }) {
  return (
    <div className="h-screen flex flex-col items-center justify-center grid-bg relative px-6">
      <div className="absolute inset-0 radial-fade" />
      <button onClick={() => navigate("/dashboard")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
      <div className="relative z-10 max-w-lg w-full text-center">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center mx-auto mb-8 glow animate-float">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Building your application</h1>
        <p className="text-slate-400 mb-10">Your AI engineering team is turning "{project.idea?.slice(0, 60)}…" into a real, working app.</p>
        <div className="glass rounded-2xl p-6 text-left space-y-3">
          {BUILD_STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              {i < step ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                : i === step ? <Loader2 className="h-4 w-4 text-indigo-400 animate-spin shrink-0" />
                : <div className="h-4 w-4 rounded-full border border-white/15 shrink-0" />}
              <span className={`text-sm font-mono ${i <= step ? "text-slate-200" : "text-slate-600"}`}>{s}</span>
            </div>
          ))}
        </div>
        <p className="font-mono text-xs text-slate-500 mt-6">Building a real, working app typically takes 1–5 minutes. You can safely leave this page — it'll be in your dashboard when it's done.</p>
      </div>
    </div>
  );
}

function ErrorView({ project, navigate, onRetry }) {
  return (
    <div className="h-screen flex flex-col items-center justify-center px-6 text-center">
      <div className="h-14 w-14 rounded-2xl bg-rose-500/10 flex items-center justify-center mb-6"><X className="h-7 w-7 text-rose-400" /></div>
      <h1 className="font-heading text-2xl font-bold mb-2">Build failed</h1>
      <p className="text-slate-400 max-w-md mb-6 font-mono text-sm">{project.error || "The AI build encountered an error."}</p>
      <div className="flex gap-3">
        <button onClick={() => navigate("/dashboard")} className="px-5 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] transition-colors text-sm font-medium">Back to dashboard</button>
        <button onClick={onRetry} className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition-colors text-sm font-medium">Refresh</button>
      </div>
    </div>
  );
}

function DatabaseView({ schema }) {
  const tables = schema?.tables || [];
  if (!tables.length) return <div className="h-full flex items-center justify-center text-slate-500">No schema generated</div>;
  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex items-center gap-2 mb-6"><Database className="h-5 w-5 text-violet-400" /><h2 className="font-heading text-xl font-semibold">Database schema</h2><span className="text-xs font-mono text-slate-500">{tables.length} tables</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {tables.map((t) => (
          <div key={t.name} className="glass rounded-xl overflow-hidden card-hover">
            <div className="px-4 py-3 border-b border-white/10 bg-violet-500/5 flex items-center gap-2"><Database className="h-4 w-4 text-violet-400" /><span className="font-mono font-semibold text-sm">{t.name}</span></div>
            <div className="p-2">
              {(t.fields || []).map((f) => (
                <div key={f.name} className="flex items-center justify-between px-2 py-1.5 text-sm">
                  <span className="font-mono text-slate-300 flex items-center gap-2">{f.key === "PK" && <span className="text-[10px] px-1 rounded bg-amber-500/20 text-amber-300">PK</span>}{f.key === "FK" && <span className="text-[10px] px-1 rounded bg-cyan-500/20 text-cyan-300">FK</span>}{f.name}</span>
                  <span className="font-mono text-xs text-slate-500">{f.type}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthView({ scores, spec, deployment }) {
  const keys = ["build", "tests", "security", "performance", "seo", "accessibility"];
  const avg = keys.reduce((a, k) => a + (scores[k] || 0), 0) / keys.length;
  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex items-center gap-2 mb-6"><Activity className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-semibold">Project health</h2></div>
      <div className="glass rounded-2xl p-6 mb-6 flex items-center gap-6">
        <div className="relative h-24 w-24 shrink-0">
          <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100"><circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" /><circle cx="50" cy="50" r="42" fill="none" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(avg / 100) * 264} 264`} /></svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-heading text-2xl font-bold">{Math.round(avg)}</span><span className="text-[10px] text-slate-500">/ 100</span></div>
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold mb-1">Overall score: Excellent</h3>
          <p className="text-slate-400 text-sm">Your build passes automated audits across all disciplines.</p>
          {deployment?.status === "live" && <p className="text-emerald-400 text-sm font-mono mt-2">● Live · {deployment.url}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {keys.map((k) => {
          const Icon = SCORE_ICONS[k] || Zap;
          const v = scores[k] || 0;
          return (
            <div key={k} className="glass rounded-xl p-5 card-hover">
              <div className="flex items-center justify-between mb-3"><span className="text-sm capitalize text-slate-300">{k}</span><Icon className="h-4 w-4 text-indigo-400" /></div>
              <div className="font-heading text-3xl font-bold mb-2">{v}<span className="text-sm text-slate-500">/100</span></div>
              <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-400" style={{ width: `${v}%` }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
